import * as vp from 'vopay';
import { Box, Title2, Table, TextField, Card, Icon, DropdownMenu, MaterialSymbol } from 'vopay';
import { ComponentProps, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchAccountUsers from '@/hooks/useApi/useFetchAccountUsers';
import userPostDeleteUser from '@/hooks/useApi/usePostDeleteAccountUser';
import DeleteUserContext, { DeleteUserCtx } from './DeleteUser/DeleteUserContext';
import DeleteUserDialogue from './DeleteUser/DeleteUserDialogue';
import { DeleteUserDialogueHandler, DialogueHandlerProps } from './types';
import { usePermissions } from '@/providers/Permissions.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { Button, Chip } from 'UI';
import useResetPassword from './useResetPassword';
import AccountUserPermissionsDrawer from './UserPermissions/AccountUserPermissionsDrawer';
import UserRolesContainer from './UserRoles/UserRolesContainer';
import CreateRoleDrawer from './UserRoles/CreateRoleDrawer';
import RoleDetailsDrawer from './UserRoles/RoleDetailsDrawer';
import EditRoleDrawer from './UserRoles/EditRoleDrawer';
import RolePermissionsViewDrawer from './UserRoles/RolePermissionsViewDrawer';
import RoleUsersViewDrawer from './UserRoles/RoleUsersViewDrawer';
import SelectRoleDialogue from './UserRoles/SelectRoleDialogue';
import CreateUserDrawer from './CreateUser/CreateUserDrawer';
import useFetchUsersByRole from '@/hooks/useApi/useFetchUsersByRole';
import useFetchAccountUserRoles from '@/hooks/useApi/useFetchAccountUserRoles';
import useDeleteAccountUserRole from '@/hooks/useApi/useDeleteAccountUserRole';
import useFetchRoleUserCounts from '@/hooks/useApi/useFetchRoleUserCounts';
import useAssignUserRole from '@/hooks/useApi/useAssignUserRole';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import {
  propagateRoleDeleteToSubAccounts,
  type PropagateRoleDeleteResult,
} from './UserRoles/propagateRoleDeleteToSubAccounts';
import vopayApi from '@/services/api.config';
import type { AccountUserRole } from 'vopay';
// Reuse the dialog typography classes already used by the Edit drawer's
// confirmation dialogs so the Delete dialog matches them visually.
import dialogStyles from './UserRoles/EditRoleDrawer.module.scss';

type ActionsMenuItem = {
  id: string;
  label: string;
  // Derive the literal-union type directly from MaterialSymbol's prop so
  // the value matches the icon font's available glyphs (`tsc --build` is
  // stricter than `tsc --noEmit` and rejects a plain `string` here).
  icon: ComponentProps<typeof MaterialSymbol>['icon'];
  colorScheme?: 'default' | 'destructive';
  onSelect: () => void;
};

type AccountUser = {
  Username: string;
  DateAdded: string;
  Roles: string;
  ActionsMenu: ActionsMenuItem[];
};

const UsersList: FC = () => {
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { resetPassword } = useResetPassword();
  const { userSettings } = useUserSettingsProvider();
  const [shouldFetchUsers, setFetchUsers] = useState(true);
  const [DeleteUserError, setDeleteUserError] = useState('');
  const [showDeleteUserErrorAlert, setShowDeleteUserErrorAlert] = useState(false);
  const [showDeleteUserSuccessAlert, setShowDeleteUserSuccessAlert] = useState(false);
  const [showDeleteUserConfirmationAlert, setshowDeleteUserConfirmationAlert] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [shouldDeleteUser, setShouldDeleteUser] = useState(false);
  const [DeleteUserResponse, deleteUser] = userPostDeleteUser({});
  const [users, fetchUsers] = useFetchAccountUsers({});
  const [filteredUsers, setFilteredUsers] = useState<vp.AccountUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { permissions } = usePermissions();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [permissionsEmail, setPermissionsEmail] = useState<string>();
  const [isCreateRoleDrawerOpen, setIsCreateRoleDrawerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<AccountUserRole | null>(null);
  const [editingRole, setEditingRole] = useState<AccountUserRole | null>(null);
  const [viewingPermissionsRole, setViewingPermissionsRole] = useState<AccountUserRole | null>(null);
  const [viewingUsersRole, setViewingUsersRole] = useState<AccountUserRole | null>(null);
  const [assignRoleUser, setAssignRoleUser] = useState<{ Username: string } | null>(null);
  // When the admin clicks "+ New Role" from inside the Edit Role(s) dialog,
  // we remember who they were editing so we can auto-assign the new role to
  // that user once the Create Role drawer finishes.
  const [pendingNewRoleAssignee, setPendingNewRoleAssignee] = useState<{ Username: string } | null>(
    null,
  );
  const [isCreateUserDrawerOpen, setIsCreateUserDrawerOpen] = useState(false);
  // In-flight Create User session paused while the admin creates a new role.
  // Non-null = "Create User drawer should reopen with this state once the
  // Create Role drawer closes." Cleared when the admin actually closes the
  // Create User drawer (submits or cancels).
  const [pendingCreateUserState, setPendingCreateUserState] = useState<{
    emailAddress: string;
    selectedRoleIds: number[];
  } | null>(null);
  const {
    fetching: fetchingRoleUsers,
    users: roleUsers,
    fetchUsersByRole,
    reset: resetRoleUsers,
  } = useFetchUsersByRole();
  const { deleteUserRole } = useDeleteAccountUserRole();
  const { assignUserRole } = useAssignUserRole();
  const { counts: roleCounts, usersByRole, fetchRoleUserCounts } = useFetchRoleUserCounts();
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});

  // Pull the user list for whichever role is currently selected. Lifted up
  // here so Role Details (count), the Users sub-drawer (list), and the Delete
  // confirmation (count) all share one fetch.
  useEffect(() => {
    if (selectedRole) {
      fetchUsersByRole(selectedRole.RoleID).catch(() => {
        // already logged in the hook
      });
    } else {
      resetRoleUsers();
    }
  }, [selectedRole?.RoleID]);

  const { roles, fetching: fetchingRoles, fetchAccountUserRoles } = useFetchAccountUserRoles();

  // Refresh per-role user counts whenever the role list changes.
  useEffect(() => {
    if (roles.length === 0) return;
    fetchRoleUserCounts(roles.map((r) => r.RoleID)).catch(() => {
      // already logged in the hook
    });
  }, [roles]);

  // When the Create Role drawer closes (success or cancel) and we're in the
  // middle of a Create User flow that diverted to + New Role, reopen the
  // Create User drawer so the admin returns to where they were. The drawer
  // hydrates its state from initialEmailAddress/initialRoleIds, which point
  // at pendingCreateUserState.
  useEffect(() => {
    if (!isCreateRoleDrawerOpen && pendingCreateUserState && !isCreateUserDrawerOpen) {
      setIsCreateUserDrawerOpen(true);
    }
  }, [isCreateRoleDrawerOpen, pendingCreateUserState, isCreateUserDrawerOpen]);

  // Invert `usersByRole` (RoleID -> users[]) into `rolesByUsername`
  // (Username -> roles[]) for rendering the Role(s) column. Keys are
  // lowercased on both insert + lookup to defend against any case
  // discrepancy between the two endpoints that supply the data
  // (/account/users vs /account/user-roles/role) — usernames are emails and
  // should be case-insensitive.
  const rolesByUsername = useMemo(() => {
    const map: Record<string, AccountUserRole[]> = {};
    for (const role of roles) {
      const usersInRole = usersByRole[role.RoleID] ?? [];
      for (const u of usersInRole) {
        const key = u.Username.toLowerCase();
        if (!map[key]) map[key] = [];
        map[key].push(role);
      }
    }
    return map;
  }, [roles, usersByRole]);

  useEffect(() => {
    if (!shouldFetchUsers) return;

    fetchUsers({
      Timezone: userSettings.timezone,
    });
  }, [shouldFetchUsers]);

  // Fetch roles once on mount (if the user can view roles).
  useEffect(() => {
    if (permissions.ViewUserRoles || permissions.ManageUserRoles) {
      fetchAccountUserRoles().catch((err) => {
        console.error('Failed to fetch user roles on mount:', err);
      });
    }
  }, [permissions.ViewUserRoles, permissions.ManageUserRoles]);
  useEffect(() => {
    const filteredUsersToSet = Object.values(users.Users || {}).filter(
      (user) => searchTerm === '' || user.Username.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredUsers(filteredUsersToSet);
  }, [searchTerm, users]);

  const handleResetPasswordDialog = async (userEmail: string) => {
    const result = await alertDialog.open(
      t('ResetPassword.title', { defaultValue: 'Send Password Reset Email?' }),
      t('ResetPassword.description', {
        defaultValue: 'This will send a secure password reset link to the user’s email address.',
      }),
      {
        abortLabel: t('ResetPassword.cancelButton', { defaultValue: 'Cancel Button' }),
        confirmLabel: t('ResetPassword.sendButton', { defaultValue: 'Send Reset Email' }),
      },
    );

    if (result === 'confirm') {
      try {
        await resetPassword({
          Username: userEmail,
        });
        toast.success(t('ResetPassword.success', { defaultValue: 'The link was sent successfully.' }));
      } catch (err) {
        console.error(err);
        toast.neutral(t('ResetPassword.fail', { defaultValue: 'Unable to send the link.' }));
      }
    }
  };

  const tableProps: vp.TableProps<AccountUser> = {
    id: 'users-list',
    version: '2',
    zebra: true,
    columns: [
      { id: 'Username', header: t('table.email') || '', align: 'left', field: 'Username' },
      { id: 'DateAdded', header: t('table.dateAdded') || '', align: 'left', field: 'DateAdded' },
      ...(roles.length > 0
        ? [
            {
              id: 'Roles',
              header: t('table.roles', { defaultValue: 'Role(s)' }) || 'Role(s)',
              align: 'left' as const,
              template: ({ data }: { data: AccountUser }) => {
                const userRoles = rolesByUsername[data.Username.toLowerCase()] ?? [];
                if (userRoles.length === 0) return <span>-</span>;
                return (
                  <Box flx="row" gapSize="6px" wrap="wrap">
                    {userRoles.map((role) => (
                      <Chip key={role.RoleID} label={role.Name} />
                    ))}
                  </Box>
                );
              },
            },
          ]
        : []),
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) =>
          data.ActionsMenu.length === 0 ? null : (
            <Box jc="fe" gap="sm">
              <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                  <Button size="small" colorScheme="secondary" iconRight="keyboard_arrow_down">
                    {t('actions.actions', { defaultValue: 'Actions' })}
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  {data.ActionsMenu.map((item) => (
                    <DropdownMenu.Item
                      key={item.id}
                      onSelect={() => item.onSelect()}
                      colorScheme={item.colorScheme}
                      left={
                        <MaterialSymbol
                          icon={item.icon}
                          size={18}
                          color={item.colorScheme === 'destructive' ? '#e8160b' : undefined}
                        />
                      }
                    >
                      {item.label}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu>
            </Box>
          ),
      },
    ],
    rows: filteredUsers.map((user) => {
      const items: ActionsMenuItem[] = [];

      if (permissions.ManageUserPermissions) {
        items.push({
          id: `user-select-role-${user.DateAdded}`,
          label: t('actions.selectRole', { defaultValue: 'Edit Role' }),
          icon: 'groups_2',
          onSelect: () => {
            setAssignRoleUser({ Username: user.Username });
          },
        });
        items.push({
          id: `user-change-Permissions-${user.DateAdded}`,
          label: t('actions.permissions'),
          icon: 'settings',
          onSelect: () => {
            setPermissionsEmail(user.Username);
            setIsPermissionsDrawerOpen(true);
          },
        });
      }

      if (permissions.AddUsers) {
        items.push({
          id: `user-change-${user.DateAdded}`,
          label: t('actions.resetPassword', { defaultValue: 'Reset Password' }),
          icon: 'refresh',
          onSelect: () => {
            handleResetPasswordDialog(user.Username);
          },
        });
        items.push({
          id: `user-delete-${user.DateAdded}`,
          label: t('actions.remove'),
          icon: 'close',
          colorScheme: 'destructive',
          onSelect: () => {
            setEmailAddress(user.Username);
            setshowDeleteUserConfirmationAlert(true);
          },
        });
      }

      return {
        id: user.Username,
        data: {
          Username: user.Username,
          DateAdded: user.DateAdded,
          Roles: '-',
          ActionsMenu: items,
        },
      };
    }),
  };

  const DeleteUser: DeleteUserCtx = {
    showDeleteUserErrorAlert,
    showDeleteUserSuccessAlert,
    showDeleteUserConfirmationAlert,
    DeleteUserError,
  };

  useEffect(() => {
    if (shouldDeleteUser) {
      setShouldDeleteUser(false);
      const { Success, ErrorMessage } = DeleteUserResponse;
      if (Success === true) {
        setShowDeleteUserSuccessAlert(true);
      } else {
        setDeleteUserError(ErrorMessage);
        setShowDeleteUserErrorAlert(true);
      }
    }
  }, [DeleteUserResponse]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<DeleteUserDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: DeleteUserDialogueHandler = {
        delete: {
          success: {
            default: () => {
              setShowDeleteUserSuccessAlert(false);
              setFetchUsers(true);
              fetchUsers({
                Timezone: userSettings.timezone,
              });
            },
          },
          fail: {
            default: () => {
              setShowDeleteUserErrorAlert(false);
              setDeleteUserError('');
            },
          },
        },
        alertDelete: {
          success: {
            default: () => {
              setshowDeleteUserConfirmationAlert(false);

              const DeleteUserRequest = {
                EmailAddress: emailAddress,
              };
              setShouldDeleteUser(true);
              deleteUser(DeleteUserRequest);
            },
          },
          fail: {
            default: () => {
              setshowDeleteUserConfirmationAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  return (
    <div>
      <DeleteUserContext.Provider value={DeleteUser}>
        <Box mb="md" jc="sb">
          <Title2 level="1">{t('title')}</Title2>
          <Box gapSize="16px" ai="center">
            <TextField
              key="UserSearchKeyword"
              name="UserSearchKeyword"
              placeholder={t('search.placeholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              variant="outlineShadow"
              elementLeft={
                <div style={{ marginLeft: '14px' }}>
                  <Icon icon="search" width="17px" height="17px" color="none" />
                </div>
              }
            />
            {permissions.AddUsers && (
              <Button
                id="showHideAccountCredentials"
                onClick={() => setIsCreateUserDrawerOpen(true)}
                colorScheme="primary"
              >
                {t('actions.addUser')}
              </Button>
            )}
          </Box>
        </Box>
        <UserRolesContainer
          onCreateRoleClick={() => setIsCreateRoleDrawerOpen(true)}
          onRoleClick={(role) => setSelectedRole(role)}
          roles={roles}
          roleCounts={roleCounts}
          fetching={fetchingRoles}
        />
        <Card
          style={{
            padding: '16px',
          }}
        >
          <Table {...tableProps} />
        </Card>
        <DeleteUserDialogue handleDialogue={handlers.handleDialogue} />
      </DeleteUserContext.Provider>
      <AccountUserPermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        emailAddress={permissionsEmail}
        availableRoles={roles}
      />
      <CreateUserDrawer
        isOpen={isCreateUserDrawerOpen}
        existingUsernames={Object.values(users.Users || {}).map((u) => u.Username)}
        availableRoles={roles}
        initialEmailAddress={pendingCreateUserState?.emailAddress}
        initialRoleIds={pendingCreateUserState?.selectedRoleIds}
        onClose={() => {
          setIsCreateUserDrawerOpen(false);
          // Drawer closing for real (submit or cancel) — discard any paused
          // session state so the next Add User click starts fresh.
          setPendingCreateUserState(null);
        }}
        onUserCreated={() => {
          setFetchUsers(true);
          fetchUsers({ Timezone: userSettings.timezone });
          // Roles + counts also need refresh in case the new user was given
          // any roles during creation (affects Role(s) column + tile counts).
          if (roles.length > 0) {
            fetchRoleUserCounts(roles.map((r) => r.RoleID)).catch(() => {
              // already logged in the hook
            });
          }
        }}
        onCreateNewRole={
          // Only show "+ New Role" for admins who can actually create role
          // definitions. We pause the Create User session (saving email +
          // role selections) and divert to Create Role. After Create Role
          // closes (success or cancel) the useEffect above reopens Create
          // User with the saved state.
          permissions.ManageUserRoles
            ? (currentState) => {
                setPendingCreateUserState(currentState);
                // Bypass onClose handler — we don't want to clear the paused
                // state here; that only happens when the drawer truly closes.
                setIsCreateUserDrawerOpen(false);
                setIsCreateRoleDrawerOpen(true);
              }
            : undefined
        }
      />
      <CreateRoleDrawer
        isOpen={isCreateRoleDrawerOpen}
        onClose={() => {
          setIsCreateRoleDrawerOpen(false);
          // Clear the pending assignee when the drawer closes (covers Cancel
          // path). Setting it again on open if needed is handled by
          // onCreateNewRole above.
          setPendingNewRoleAssignee(null);
        }}
        suppressSuccessToast={!!pendingNewRoleAssignee}
        onRoleCreated={async (newRole) => {
          // Always refresh the roles list.
          fetchAccountUserRoles().catch((err) => {
            console.error('Failed to refresh user roles after create:', err);
          });

          // Came from the Create User drawer's "+ New Role" chain — append
          // the new role to the paused session so it's pre-selected when
          // Create User reopens.
          if (pendingCreateUserState) {
            setPendingCreateUserState({
              emailAddress: pendingCreateUserState.emailAddress,
              selectedRoleIds: [...pendingCreateUserState.selectedRoleIds, newRole.RoleID],
            });
            return;
          }

          // Auto-assign the new role to the user we were editing, when this
          // flow originated from the Edit Role(s) dialog.
          if (pendingNewRoleAssignee) {
            const assignee = pendingNewRoleAssignee;
            setPendingNewRoleAssignee(null);
            try {
              await assignUserRole({
                Username: assignee.Username,
                RoleID: newRole.RoleID,
              });
              toast.success(
                t('actions.roleCreatedAndAssigned', {
                  defaultValue: `Role "${newRole.Name}" was created and assigned to ${assignee.Username}.`,
                }),
              );
              // Refresh role->user mapping so the Role(s) column + tile count
              // both reflect the new assignment immediately.
              fetchRoleUserCounts(
                [...roles.map((r) => r.RoleID), newRole.RoleID],
              ).catch(() => {
                // already logged in the hook
              });
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Unknown error';
              // Error severity (not neutral): the role exists but the
              // expected chained assignment didn't land — admin needs to
              // notice, since recovery is manual via Edit Role.
              alertDialog.error(
                t('actions.roleAssignFailedTitle', { defaultValue: 'Role created but not assigned' }),
                t('actions.roleAssignFailed', {
                  defaultValue: `Role "${newRole.Name}" was created, but could not be assigned to ${assignee.Username}: ${message}\n\nYou can assign it manually from Actions → Edit Role.`,
                }),
              );
            }
          }
        }}
      />
      <RoleDetailsDrawer
        isOpen={!!selectedRole && !editingRole}
        role={selectedRole}
        usersCount={roleUsers.length}
        fetchingUsers={fetchingRoleUsers}
        onClose={() => setSelectedRole(null)}
        onEdit={() => {
          if (selectedRole) setEditingRole(selectedRole);
        }}
        onDelete={async () => {
          if (!selectedRole) return;
          const roleToDelete = selectedRole;
          const usersAffected = roleUsers.length;

          // When the role propagates to sub-accounts, pre-fetch the sub-account
          // count so the dialog can show "Removed from <y> sub-accounts".
          let subAccountsCount = 0;
          const propagatesToSubAccounts =
            roleToDelete.ApplyToAllSubaccounts && !!permissions.ViewSubaccounts;
          if (propagatesToSubAccounts) {
            try {
              const res = await fetchSubAccountsAsync({ ShowToken: true, ActiveOnly: true });
              subAccountsCount = res.Success ? res.Subaccounts?.length ?? 0 : 0;
            } catch (err) {
              console.error('Failed to fetch sub-accounts for delete dialog:', err);
            }
          }

          const confirmation = await alertDialog.caution(
            t('deleteRoleTitle', { defaultValue: 'Delete Role?' }),
            propagatesToSubAccounts ? (
              <div data-apply-confirm-dialog="delete-role-cascade">
                <p className={dialogStyles.confirmDialogLead}>
                  {t('deleteRoleCascadeLead', {
                    defaultValue: 'Are you sure you want to delete this role? This role will be:',
                  })}
                </p>
                <ul className={dialogStyles.confirmDialogList}>
                  <li>
                    {t('deleteRoleCascadeBullet1', {
                      defaultValue: `Removed from ${subAccountsCount} sub-accounts`,
                      count: subAccountsCount,
                    })}
                  </li>
                  <li>
                    {t('deleteRoleCascadeBullet2', {
                      defaultValue: `Unassigned from ${usersAffected} users`,
                      count: usersAffected,
                    })}
                  </li>
                </ul>
              </div>
            ) : (
              <div data-apply-confirm-dialog="delete-role">
                <p className={dialogStyles.confirmDialogLead}>
                  {t('deleteRoleBody', {
                    defaultValue: `Are you sure you want to delete this role? This role will be unassigned from ${usersAffected} user${usersAffected === 1 ? '' : 's'}.`,
                    count: usersAffected,
                  })}
                </p>
              </div>
            ),
            {
              abortLabel: t('goBack', { defaultValue: 'Go Back' }),
              confirmLabel: tGlobal('delete'),
            },
          );

          if (confirmation !== 'confirm') return;

          try {
            await deleteUserRole({ RoleID: roleToDelete.RoleID });

            // Fan out the delete to sub-accounts if the role was propagated.
            // Each sub-account has its own RoleID, looked up by Name.
            let subResult: PropagateRoleDeleteResult | null = null;
            if (roleToDelete.ApplyToAllSubaccounts && permissions.ViewSubaccounts) {
              subResult = await propagateRoleDeleteToSubAccounts(roleToDelete.Name, {
                fetchSubAccountsAsync,
                fetchAccountUserRolesWithBearer: (bearer) =>
                  vopayApi.api.fetchAccountUserRoles(undefined, bearer),
                deleteUserRole,
              });
            }

            // Close drawers, refresh, and toast.
            setSelectedRole(null);
            setEditingRole(null);
            setViewingPermissionsRole(null);
            setViewingUsersRole(null);
            fetchAccountUserRoles().catch((err) => {
              console.error('Failed to refresh user roles after delete:', err);
            });

            if (subResult && (subResult.failed.length > 0 || subResult.skipped.length > 0)) {
              toast.neutral(
                t('deleteRolePartial', {
                  defaultValue: `Role "${roleToDelete.Name}" deleted. Removed from ${subResult.numDeleted} of ${subResult.numTotal} sub-accounts (${subResult.failed.length} failed, ${subResult.skipped.length} skipped).`,
                }),
              );
            } else {
              toast.success(
                t('deleteRoleSuccess', {
                  defaultValue: `Role "${roleToDelete.Name}" was deleted successfully.`,
                }),
              );
            }
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            alertDialog.error(
              t('deleteRoleErrorTitle', { defaultValue: 'Could not delete role' }),
              message,
            );
          }
        }}
        onViewPermissions={() => {
          if (selectedRole) setViewingPermissionsRole(selectedRole);
        }}
        onViewUsers={() => {
          if (selectedRole) setViewingUsersRole(selectedRole);
        }}
      />
      <RolePermissionsViewDrawer
        isOpen={!!viewingPermissionsRole}
        role={viewingPermissionsRole}
        onClose={() => setViewingPermissionsRole(null)}
      />
      <RoleUsersViewDrawer
        isOpen={!!viewingUsersRole}
        role={viewingUsersRole}
        users={roleUsers}
        fetching={fetchingRoleUsers}
        onClose={() => setViewingUsersRole(null)}
      />
      <SelectRoleDialogue
        isOpen={!!assignRoleUser}
        user={assignRoleUser}
        availableRoles={roles}
        onClose={() => setAssignRoleUser(null)}
        onAssignmentsChanged={() => {
          // Refresh tile counts so the count for any newly-assigned role bumps.
          if (roles.length > 0) {
            fetchRoleUserCounts(roles.map((r) => r.RoleID)).catch(() => {
              // already logged in the hook
            });
          }
        }}
        onCreateNewRole={
          // The "+ New Role" footer button only makes sense for admins who
          // can actually create a role definition (ManageUserRoles). Without
          // the callback, SelectRoleDialogue hides the footer entirely.
          permissions.ManageUserRoles
            ? () => {
                // Remember the user so we can auto-assign the new role to them
                // after the Create Role drawer succeeds.
                setPendingNewRoleAssignee(assignRoleUser);
                setAssignRoleUser(null);
                setIsCreateRoleDrawerOpen(true);
              }
            : undefined
        }
      />
      <EditRoleDrawer
        isOpen={!!editingRole}
        role={editingRole}
        onClose={() => {
          setEditingRole(null);
          setSelectedRole(null);
        }}
        onRoleUpdated={() => {
          setSelectedRole(null);
          fetchAccountUserRoles().catch((err) => {
            console.error('Failed to refresh user roles after update:', err);
          });
        }}
      />
    </div>
  );
};
export default UsersList;
